import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { TextArea } from '@shared/components/ui/textarea';
import { useCouriersDropdown } from '@modules/CODPayments/CourierFiles/List/hooks/useCouriersDropdown';
import { useCreateCourierInvoice } from '../../hooks/useCreateCourierInvoice';
import type { CreateCourierInvoiceBody } from '../../../models/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CreateCourierInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (id: number) => void;
}

const emptyForm = () => ({
  courier_id: '',
  supplier_reference: '',
  invoiced_at: '',
  period_start: '',
  period_end: '',
  currency: 'EUR',
  notes: '',
});

export const CreateCourierInvoiceModal = ({ isOpen, onClose, onCreated }: CreateCourierInvoiceModalProps) => {
  const { t } = useTranslation('courierInvoicesModule');
  const [form, setForm] = useState(emptyForm);
  const { couriers, isLoading: loadingCouriers } = useCouriersDropdown();
  const { createInvoiceAsync, isCreating } = useCreateCourierInvoice();

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyForm());
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const cid = Number(form.courier_id);
    if (!cid) {
      return;
    }
    const body: CreateCourierInvoiceBody = {
      courier_id: cid,
      ...(form.supplier_reference.trim() ? { supplier_reference: form.supplier_reference.trim() } : {}),
      ...(form.invoiced_at ? { invoiced_at: form.invoiced_at } : {}),
      ...(form.period_start ? { period_start: form.period_start } : {}),
      ...(form.period_end ? { period_end: form.period_end } : {}),
      ...(form.currency.trim() ? { currency: form.currency.trim() } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
    try {
      const res = await createInvoiceAsync(body);
      onCreated?.(res.id);
      onClose();
    } catch {
      /* toast en useCreateCourierInvoice */
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg bg-background border-border text-foreground transition-colors">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t('create.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>{t('create.courier')}</Label>
            <Select
              value={form.courier_id}
              onValueChange={(v) => setForm((s) => ({ ...s, courier_id: v }))}
              disabled={loadingCouriers || isCreating}
            >
              <SelectTrigger className="bg-background border-border transition-colors">
                <SelectValue placeholder={t('create.courier_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {couriers.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>{t('create.supplier_reference')}</Label>
            <Input
              value={form.supplier_reference}
              onChange={(e) => setForm((s) => ({ ...s, supplier_reference: e.target.value }))}
              disabled={isCreating}
              className="bg-background border-border transition-colors"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>{t('create.invoiced_at')}</Label>
              <Input
                type="date"
                value={form.invoiced_at}
                onChange={(e) => setForm((s) => ({ ...s, invoiced_at: e.target.value }))}
                disabled={isCreating}
                className="bg-background border-border transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('create.currency')}</Label>
              <Input
                value={form.currency}
                onChange={(e) => setForm((s) => ({ ...s, currency: e.target.value }))}
                disabled={isCreating}
                className="bg-background border-border transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>{t('create.period_start')}</Label>
              <Input
                type="date"
                value={form.period_start}
                onChange={(e) => setForm((s) => ({ ...s, period_start: e.target.value }))}
                disabled={isCreating}
                className="bg-background border-border transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('create.period_end')}</Label>
              <Input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm((s) => ({ ...s, period_end: e.target.value }))}
                disabled={isCreating}
                className="bg-background border-border transition-colors"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>{t('create.notes')}</Label>
            <TextArea
              value={form.notes}
              onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
              disabled={isCreating}
              rows={3}
              className="bg-background border-border transition-colors resize-y min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isCreating}>
            {t('create.cancel')}
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={isCreating || !form.courier_id}>
            {t('create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
